'use client'

import { useState } from 'react'
import { Company } from '@/shared/types/company'
import { Button } from '@/shared/ui/button/button'
import { ContactModal } from '@/shared/ui/contact-modal/contact-modal'
import * as Dialog from '@radix-ui/react-dialog'
import { X, Calendar, Building2, TrendingUp, Check, Shield, FileText } from 'lucide-react'
import { motion } from 'framer-motion'
import { getDisplayTaxSystem } from '@/shared/lib/tax-system'

interface CompanyModalProps {
  company: Company | null
  isOpen: boolean
  onClose: () => void
}

export const CompanyModal = ({ company, isOpen, onClose }: CompanyModalProps) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)

  if (!company) return null

  const handleContentClick = (e: React.MouseEvent) => {
    // Закрываем модальное окно только если клик был на фон, а не на контент
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0
    }).format(price)
  }

  const formatAmount = (amount: number) => {
    return `${amount} млн`
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/80" />
        <Dialog.Content
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={handleContentClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative bg-background rounded-xl border shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Заголовок */}
            <div className="sticky top-0 bg-background border-b px-6 py-4 flex flex-row items-center justify-between">
              <Dialog.Title className="text-2xl font-bold text-foreground">
                {company.name}
              </Dialog.Title>
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <X className="h-4 w-4" />
                </Button>
              </Dialog.Close>
            </div>

            <div className="p-6 space-y-6">
              {/* Основная информация */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Левая колонка */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Основная информация
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 px-3 py-1 rounded-md font-medium">
                          {getDisplayTaxSystem(company.taxSystem, company.usnRate)}
                        </span>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          Дата регистрации: {company.registrationDate}
                        </span>
                      </div>
                      <div className="mt-3">
                        <p className="text-sm font-medium mb-1">ОКВЭД:</p>
                        <p className="text-sm text-muted-foreground">
                          {company.okvedCode} {company.okvedDescription}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Банковские счета */}
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Банковские счета
                    </h4>
                    <div>
                      <p className="text-sm text-muted-foreground truncate">
                        {company.bankAccounts.join(', ')}
                      </p>
                    </div>
                  </div>

                  {/* Уставный капитал */}
                  {/* Уставный капитал скрыт */}
                </div>

                {/* Правая колонка */}
                <div className="space-y-4">
                  {/* Обороты */}
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Обороты по годам
                    </h4>
                    <div className="space-y-2">
                      {company.turnover
                        .sort((a, b) => b.year - a.year)
                        .map((item) => (
                          <div
                            key={item.year}
                            className="flex justify-between items-center py-2 px-3 bg-muted/50 rounded-md"
                          >
                            <span className="font-medium">{item.year}</span>
                            <span className="text-lg font-semibold text-orange-600">
                              {formatAmount(item.amount)}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Стоимость */}
                  <div className="bg-orange-50 dark:bg-orange-900/10 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Стоимость компании</h4>
                    <p className="text-2xl font-bold text-orange-600">
                      {formatPrice(company.price)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Верификация */}
              {company.isVerified && (
                <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 dark:bg-green-900/20 p-2 rounded-full">
                      <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-green-800 dark:text-green-200 mb-1">
                        Проверка пройдена
                      </h4>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        В черных списках ЦБ, ЗСК, 115 и 764-П (экс 550п, 639п) не найдено!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Описание */}
              {company.fullDescription && (
                <div>
                  <h4 className="font-medium mb-2">Описание деятельности</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {company.fullDescription}
                  </p>
                </div>
              )}

              {/* Преимущества */}
              {company.benefits && company.benefits.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Преимущества
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {company.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Кнопки действий */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                <Button
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  onClick={() => setIsContactModalOpen(true)}
                >
                  Связаться с нами
                </Button>
                <Button variant="outline" className="flex-1">
                  Скачать документы
                </Button>
              </div>
            </div>
          </motion.div>
        </Dialog.Content>
      </Dialog.Portal>

      {/* Модалка контактов */}
      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </Dialog.Root>
  )
}
